import { get } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';
import type { CalendarData, PricesByDays, BookableDatesData } from 'Store/calendar/calendar.helper';
import {
  INITIAL_MONTH,
  getDefaultSelectedMonth,
  getPricesByDays,
  getSelectedMonth,
} from 'Store/calendar/calendar.helper';
import {
  FETCH_CALENDAR,
  FETCH_CALENDAR_TRIP_PRICE,
  FETCH_BOOKABLE_DATES,
  CALENDAR_CHANGE_MONTH,
  CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE,
  CALENDAR_SET_INITIAL_DURATION,
  CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION,
  CLEAR_CALENDAR,
  TRIP_INFORMATION_SET_DEPARTURE_DATE,
  SYNC_WITH_LOCATION,
} from 'Store/constants';
import createReducer from 'Store/utils/createReducer';
import { QUERY_DEPARTURE_DATE, QUERY_DATE_FORMAT } from 'Utils/uri/constants';
type CalendarRequestResolvedAction = OptimisticResolvedAction<{
  calendar: CalendarData;
}>;
type BookableDatesRequestResolvedAction = OptimisticResolvedAction<{
  bookableDates: BookableDatesData;
}>;
export type CalendarState = {
  blockInputDepartureDate: boolean;
  bookableDateLastRequestId: number;
  bookableDates: BookableDatesData;
  bookableDatesCriteria: any;
  byDays: PricesByDays;
  calendarCriteria: any;
  calendar: CalendarData;
  departureCity: string | null | undefined;
  initialDuration: number | null | undefined;
  errorResponse: {
    errors?: any;
  };
  isLoading: boolean;
  isPriceCalendarSelection: boolean;
  lastRequestId: number;
  productId: string | null | undefined;
  tripPrice: ProductPrice;
  tripPriceIsLoading: boolean;
  tripPriceLastRequestId: number;
  selectedMonth: number;
};
export const initialState: CalendarState = {
  blockInputDepartureDate: true,
  bookableDateLastRequestId: -1,
  bookableDates: {},
  bookableDatesCriteria: {},
  byDays: {},
  calendar: {},
  calendarCriteria: {},
  departureCity: null,
  initialDuration: null,
  isLoading: true,
  isPriceCalendarSelection: false,
  errorResponse: {},
  selectedMonth: getDefaultSelectedMonth(),
  lastRequestId: -1,
  productId: null,
  tripPrice: {},
  tripPriceLastRequestId: -1,
  tripPriceIsLoading: false,
};
export const calendarReducer = createReducer(initialState, {
  [SYNC_WITH_LOCATION](
    state: CalendarState,
    action: {
      payload: LocationChangePayload;
    },
  ): CalendarState {
    const {
      payload: {
        location: { queryParams = {} },
      },
    } = action;

    if (!queryParams[QUERY_DEPARTURE_DATE]) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(
      queryParams[QUERY_DEPARTURE_DATE] || state.selectedMonth,
      QUERY_DATE_FORMAT,
    );
    return { ...state, selectedMonth };
  },

  [FETCH_CALENDAR](state: CalendarState, action: OptimisticAction<{}, {}>): CalendarState {
    return {
      ...state,
      bookableDates: {},
      // @ts-expect-error TODO(flow-migration)
      calendarCriteria: action.payload.options || {},
      // @ts-expect-error TODO(flow-migration)
      departureCity: action?.payload?.departureCity,
      isLoading: true,
      lastRequestId: get(action, 'optimist.id', -1),
      // @ts-expect-error TODO(flow-migration)
      productId: action?.payload?.productId,
    };
  },

  [resolve(FETCH_CALENDAR)](
    state: CalendarState,
    action: CalendarRequestResolvedAction,
  ): CalendarState {
    const {
      optimist: { id = -1 } = {},
      payload: { calendar },
    } = action;

    // If there is another request in progress
    if (id !== state.lastRequestId || !calendar) {
      return state;
    }

    const { selectedMonth } = state;
    return {
      ...state,
      byDays: getPricesByDays(calendar),
      calendar,
      selectedMonth: getSelectedMonth(calendar, selectedMonth),
      isLoading: false,
    };
  },

  [reject(FETCH_CALENDAR)](state: CalendarState, action: OptimisticRejectedAction): CalendarState {
    const errors = action.payload.errors || '';
    return {
      ...state,
      byDays: {},
      errorResponse: {
        errors,
      },
      isLoading: false,
    };
  },

  [FETCH_BOOKABLE_DATES](state: CalendarState, action: OptimisticAction<{}, {}>): CalendarState {
    return {
      ...state,
      // @ts-expect-error TODO(flow-migration)
      bookableDatesCriteria: action.payload.options || {},
      bookableDatesLoading: true,
      bookableDateLastRequestId: get(action, 'optimist.id', -1),
    };
  },

  [resolve(FETCH_BOOKABLE_DATES)](
    state: CalendarState,
    action: BookableDatesRequestResolvedAction,
  ): CalendarState {
    const {
      optimist: { id = -1 } = {},
      payload: { bookableDates },
    } = action;

    if (id !== state.bookableDateLastRequestId || !bookableDates) {
      return state;
    }

    return { ...state, bookableDates, bookableDatesLoading: false };
  },

  [reject(FETCH_BOOKABLE_DATES)](
    state: CalendarState,
    action: OptimisticRejectedAction,
  ): CalendarState {
    const errors = action.payload.errors || '';
    return {
      ...state,
      bookableDates: {},
      errorResponse: {
        errors,
      },
      bookableDatesLoading: false,
    };
  },

  [CALENDAR_CHANGE_MONTH](
    state: CalendarState,
    action: Action<{
      selectedMonth: number;
    }>,
  ): CalendarState {
    const selectedMonth = action.payload.selectedMonth || INITIAL_MONTH;
    return { ...state, selectedMonth };
  },

  [CLEAR_CALENDAR](): CalendarState {
    return initialState;
  },

  [TRIP_INFORMATION_SET_DEPARTURE_DATE](
    state: CalendarState,
    action: Action<{
      departureDate: string;
    }>,
  ): CalendarState {
    if (!action.payload.departureDate) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(action.payload.departureDate);
    return { ...state, selectedMonth };
  },

  [CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE](
    state: CalendarState,
    action: Action<{
      blockInputDepartureDate: boolean;
    }>,
  ): CalendarState {
    return { ...state, blockInputDepartureDate: action.payload.blockInputDepartureDate };
  },

  [CALENDAR_SET_INITIAL_DURATION](
    state: CalendarState,
    action: Action<{
      duration: number;
    }>,
  ): CalendarState {
    return { ...state, initialDuration: action.payload.duration };
  },

  [CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION](
    state: CalendarState,
    action: Action<{
      isPriceCalendarSelection: boolean;
    }>,
  ): CalendarState {
    return { ...state, isPriceCalendarSelection: action.payload.isPriceCalendarSelection };
  },

  [FETCH_CALENDAR_TRIP_PRICE](state: CalendarState, action: OptimisticAction<{}, {}>) {
    return {
      ...state,
      tripPriceIsLoading: true,
      tripPriceLastRequestId: get(action, 'optimist.id', -1),
    };
  },

  [resolve(FETCH_CALENDAR_TRIP_PRICE)](
    state: CalendarState,
    action: PriceTicketRequestResolvedAction,
  ): ReduxState {
    const { optimist: { id = -1 } = {}, payload: { price: tripPrice } = {} } = action;

    if (id !== state.tripPriceLastRequestId) {
      return state;
    }

    return { ...state, tripPrice, tripPriceIsLoading: false };
  },

  [reject(FETCH_CALENDAR_TRIP_PRICE)](state: ReduxState): ReduxState {
    return { ...state, tripPrice: {}, tripPriceIsLoading: false };
  },
});
